'use client';

import { useCallback, useLayoutEffect, useRef, type RefObject } from 'react';

interface ListNavigationScrollOptions {
  sectionRef: RefObject<HTMLElement | null>;
  committedSearch: string;
  isUpdating: boolean;
  topOffset: number;
  scopeKey: string;
}

interface PendingListScroll {
  urlKey: string;
  queryKey: string;
  frameId: number | null;
  dispose: () => void;
}

function getQueryKey(search: string): string {
  const params = new URLSearchParams(search);
  params.sort();
  return params.toString();
}

function getUrlKey(url: URL): string {
  return `${url.origin}${url.pathname}?${getQueryKey(url.search)}${url.hash}`;
}

/** 仅校正本组件主动导航的最终位置，不接管框架路由或浏览器历史滚动。 */
export function useListNavigationScroll({
  sectionRef,
  committedSearch,
  isUpdating,
  topOffset,
  scopeKey,
}: ListNavigationScrollOptions): (url: string) => void {
  const pendingRef = useRef<PendingListScroll | null>(null);
  const committedQueryKey = getQueryKey(committedSearch);

  const cancelPendingScroll = useCallback(() => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    pending?.dispose();
  }, []);

  const prepareNavigationScroll = useCallback(
    (href: string) => {
      cancelPendingScroll();
      const url = new URL(href, window.location.href);
      if (url.origin !== window.location.origin) return;

      const cancelOnSemanticClick = (event: MouseEvent) => {
        // 指针手势已由 pointerdown 处理；保留 Slider mouseup 提交后同一手势的 click。
        if (event.detail === 0) cancelPendingScroll();
      };
      const cancelOnKeyDown = (event: KeyboardEvent) => {
        if (
          !event.defaultPrevented &&
          [
            'ArrowUp',
            'ArrowDown',
            'PageUp',
            'PageDown',
            'Home',
            'End',
            ' ',
            'Tab',
            'Enter',
          ].includes(event.key)
        ) {
          cancelPendingScroll();
        }
      };
      const pending: PendingListScroll = {
        urlKey: getUrlKey(url),
        queryKey: getQueryKey(url.search),
        frameId: null,
        dispose: () => {
          if (pending.frameId !== null) window.cancelAnimationFrame(pending.frameId);
          window.removeEventListener('wheel', cancelPendingScroll);
          window.removeEventListener('touchmove', cancelPendingScroll);
          window.removeEventListener('pointerdown', cancelPendingScroll, true);
          window.removeEventListener('click', cancelOnSemanticClick, true);
          window.removeEventListener('keydown', cancelOnKeyDown, true);
          window.removeEventListener('popstate', cancelPendingScroll);
          window.removeEventListener('hashchange', cancelPendingScroll);
        },
      };
      pendingRef.current = pending;

      // 只在本次导航存续期间监听；新的用户交互（含拖动滚动条）优先于自动定位。
      window.addEventListener('wheel', cancelPendingScroll, { passive: true });
      window.addEventListener('touchmove', cancelPendingScroll, { passive: true });
      window.addEventListener('pointerdown', cancelPendingScroll, { passive: true, capture: true });
      // 捕获阶段先取消旧意图，避免同一点击/按键的冒泡阶段误取消业务刚登记的新导航。
      window.addEventListener('click', cancelOnSemanticClick, { passive: true, capture: true });
      window.addEventListener('keydown', cancelOnKeyDown, { capture: true });
      window.addEventListener('popstate', cancelPendingScroll);
      window.addEventListener('hashchange', cancelPendingScroll);
    },
    [cancelPendingScroll],
  );

  // 列表、身份或货币上下文变化及卸载后，旧导航不能再影响当前页面。
  useLayoutEffect(() => cancelPendingScroll, [cancelPendingScroll, scopeKey]);

  useLayoutEffect(() => {
    const pending = pendingRef.current;
    if (!pending || isUpdating) return;
    if (pending.queryKey !== committedQueryKey) {
      cancelPendingScroll();
      return;
    }

    // 等本次 DOM commit（含 Next 祖先组件的滚动处理）结束，只检查一帧，不轮询。
    pending.frameId = window.requestAnimationFrame(() => {
      if (pendingRef.current !== pending) return;
      const isCurrentUrl = getUrlKey(new URL(window.location.href)) === pending.urlKey;
      cancelPendingScroll();

      const section = sectionRef.current;
      if (!isCurrentUrl || !section) return;
      const currentTop = window.scrollY;
      const maxTop = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      const targetTop = Math.max(
        0,
        Math.min(maxTop, currentTop + section.getBoundingClientRect().top - topOffset),
      );
      if (Math.abs(targetTop - currentTop) > 1) {
        window.scrollTo({ top: targetTop, behavior: 'instant' });
      }
    });

    return () => {
      if (pending.frameId !== null) window.cancelAnimationFrame(pending.frameId);
      pending.frameId = null;
    };
  }, [cancelPendingScroll, committedQueryKey, isUpdating, sectionRef, topOffset]);

  return prepareNavigationScroll;
}
